import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import { ArrowBack } from "@mui/icons-material";
import { Typography } from "@mui/material";
import CustomTable from "../../core/custom-table";
import dayjs from "dayjs"; // Importing dayjs to format dates
import AppContext from "../../utils/context";
import { USER_ROLES } from "../../utils/constants";

const EventMonthEnrollment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { events } = location.state || {};
  const [groupedTrainings, setGroupedTrainings] = useState({});
  const { userDetails = {} } = useContext(AppContext);

  useEffect(() => {
    if (events) {
      filterUpcomingTrainings(events);
    }
  }, [events]);

  // Format Date Range (Start Date - End Date)
  const formatDateRange = (startDate, endDate) => {
    const start = dayjs(startDate).format("DD MMM YYYY");
    const end = dayjs(endDate).subtract(1, "day").format("DD MMM YYYY"); // Subtract 1 day from end date to avoid overflow to next day
    return start === end ? start : `${start} - ${end}`;
  };

  // Calculate Training Days Length
  const calculateTrainingDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
  };

  const filterUpcomingTrainings = (events) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let groupedData = {};

    events.forEach((training) => {
      const startDate = new Date(
        training.extendedProps.courseDuration.startDate
      );
      const trainingMonth = startDate.getMonth();
      const trainingYear = startDate.getFullYear();

      // Ensure training falls in current month, next month, or next+1 month
      const isUpcoming =
        (trainingYear === currentYear &&
          trainingMonth >= currentMonth &&
          trainingMonth <= currentMonth + 2) ||
        (trainingYear === currentYear + 1 &&
          currentMonth >= 10 &&
          trainingMonth <= currentMonth - 10);

      if (isUpcoming) {
        const key = `${trainingYear}-${String(trainingMonth + 1).padStart(
          2,
          "0"
        )}`; // Format as YYYY-MM

        if (!groupedData[key]) {
          groupedData[key] = [];
        }
        groupedData[key].push(training);
      }
    });

    // Sort grouped data keys in ascending order to ensure correct order
    const sortedGroupedData = Object.keys(groupedData)
      .sort()
      .reduce((acc, key) => {
        acc[key] = groupedData[key];
        return acc;
      }, {});

    setGroupedTrainings(sortedGroupedData);
  };

  const tableConfig = {
    HeaderConfig: [
      {
        label: "Date",
        render: (data) =>
          formatDateRange(
            data.extendedProps.courseDuration.startDate,
            data.extendedProps.courseDuration.endDate
          ),
      },
      {
        label: "Name",
        render: (data) => data?.extendedProps?.name || "N/A",
      },
      {
        label: "Number of Days",
        render: (data) =>
          `${calculateTrainingDays(
            data.extendedProps.courseDuration.startDate,
            data.extendedProps.courseDuration.endDate
          )} days`,
      },
    ],
  };

  const handleBackNavigation = () => {
    if (userDetails?.role == USER_ROLES.ADMIN) {
      navigate("/admin/schedule-event");
    } else {
      navigate("/oem/schedule-event");
    }
  };

  return (
    <>
      <div className="training-title-container">
        <ArrowBack fontSize="small" onClick={() => handleBackNavigation()} />
        <div>
          <span className="training-title">Training</span>
          <div className="training-schedule">Three months schedule</div>
        </div>
      </div>
      {Object.keys(groupedTrainings).length ? (
        <div className="month-view-container2">
          {Object.keys(groupedTrainings).map((monthKey) => {
            const [year, month] = monthKey.split("-");
            const monthName = new Date(year, month - 1).toLocaleString(
              "en-GB",
              {
                month: "long",
                year: "numeric",
              }
            );

            return (
              <div key={monthKey} style={{ marginBottom: "30px" }}>
                <h2>{monthName}</h2>
                <CustomTable
                  header={tableConfig.HeaderConfig}
                  data={groupedTrainings[monthKey]}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <Typography
          sx={{ textAlign: "center", mt: 3, color: "gray" }}
          variant="h4"
        >
          No Records
        </Typography>
      )}
    </>
  );
};

export default EventMonthEnrollment;
